import { Editor } from '@tiptap/react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  CheckSquare2,
  Code,
  Columns3,
  Heading1,
  Heading2,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Redo,
  Rows3,
  Table2,
  Trash2,
  Underline,
  Undo,
  UserCheck,
} from 'lucide-react';
import { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { IconTooltip } from '../ui/IconTooltip';

interface Props {
  editor: Editor | null;
  onImageUpload: () => void;
}

const formatAuthoredDate = (date: Date): string => {
  const pad = (value: number) => value.toString().padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds} PST`;
};

const normalizeLinkHref = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const EnhancedEditorToolbar: FC<Props> = ({ editor, onImageUpload }) => {
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkInputValue, setLinkInputValue] = useState('');
  const [linkPopoverRect, setLinkPopoverRect] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const linkInputWrapperRef = useRef<HTMLDivElement>(null);
  const linkPopoverRef = useRef<HTMLDivElement>(null);
  const linkButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inLinkWrapper =
        linkInputWrapperRef.current?.contains(target) ?? false;
      const inLinkPopover = linkPopoverRef.current?.contains(target) ?? false;
      if (!inLinkWrapper && !inLinkPopover) {
        setShowLinkInput(false);
        setLinkPopoverRect(null);
      }
    };

    if (showLinkInput) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLinkInput]);

  if (!editor) return null;

  const isInTable = editor.isActive('table');
  const currentLinkHref = editor.getAttributes('link').href;
  const normalizedCurrentLinkHref =
    typeof currentLinkHref === 'string' ? currentLinkHref : '';

  const applyLinkFromInput = () => {
    const href = normalizeLinkHref(linkInputValue);
    if (!href) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    setShowLinkInput(false);
    setLinkPopoverRect(null);
  };

  const openLinkPopover = () => {
    setLinkInputValue(normalizedCurrentLinkHref);
    const rect = linkButtonRef.current?.getBoundingClientRect();
    if (rect)
      setLinkPopoverRect({ top: rect.bottom + 4, left: rect.left });
    setShowLinkInput(true);
  };

  const closeLinkPopover = () => {
    setShowLinkInput(false);
    setLinkPopoverRect(null);
  };

  // Text alignment functions
  const setTextAlign = (alignment: string) => {
    if (alignment === 'left') {
      editor.chain().focus().setTextAlign('left').run();
    } else if (alignment === 'center') {
      editor.chain().focus().setTextAlign('center').run();
    } else if (alignment === 'right') {
      editor.chain().focus().setTextAlign('right').run();
    } else if (alignment === 'justify') {
      editor.chain().focus().setTextAlign('justify').run();
    }
  };

  const insertAuthored = () => {
    // TODO: Replace with actual SOP data when backend integration is ready
    const now = new Date();
    const authoredData = {
      name: 'User',
      avatar: '/api/placeholder/32/32', // Placeholder avatar URL
      date: formatAuthoredDate(now)
    };

    editor.commands.insertAuthoredContent(authoredData.name, authoredData.avatar, authoredData.date, "Authored by");
  };

  return (
    <div className="sticky top-0 z-10 flex flex-wrap gap-2 p-2 border-b border-gray-700 bg-gray-800/95 rounded-t-lg">
      {/* Text Formatting */}
      <div className="flex gap-1 border-r border-gray-600 pr-2">
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive('bold') ? 'bg-gray-700' : ''}`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          aria-label="Bold"
        >
          <Bold className="w-4 h-4" />
        </Button>
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive('italic') ? 'bg-gray-700' : ''}`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          aria-label="Italic"
        >
          <Italic className="w-4 h-4" />
        </Button>
        <Button
          className={`btn btn-ghost p-1 ${editor.isActive('underline') ? 'bg-gray-700' : ''}`}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          aria-label="Underline"
        >
          <Underline className="w-4 h-4" />
        </Button>
        <div className="relative" ref={linkInputWrapperRef}>
          <Button
            ref={linkButtonRef}
            className={`btn btn-ghost p-1 ${editor.isActive('link') ? 'bg-gray-700' : ''}`}
            onClick={() => {
              if (editor.isActive('link')) {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                closeLinkPopover();
                return;
              }
              if (!editor.state.selection.empty) openLinkPopover();
            }}
            aria-label="Link"
            title="Add link"
          >
            <Link2 className="w-4 h-4" />
          </Button>
          {showLinkInput &&
            linkPopoverRect &&
            createPortal(
              <div
                ref={linkPopoverRef}
                className="w-64 rounded-lg border border-gray-600 bg-gray-800 p-2 shadow-lg"
                style={{
                  position: 'fixed',
                  top: linkPopoverRect.top,
                  left: linkPopoverRect.left,
                  zIndex: 9999,
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  value={linkInputValue}
                  onChange={(event) => setLinkInputValue(event.target.value)}
                  placeholder="https://example.com"
                  className="w-full rounded border border-gray-600 bg-gray-900 px-2 py-1 text-xs text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      applyLinkFromInput();
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      closeLinkPopover();
                    }
                  }}
                />
                <div className="mt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-xs text-gray-300 hover:bg-gray-700"
                    onClick={closeLinkPopover}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-xs text-blue-300 hover:bg-blue-500/20"
                    onClick={applyLinkFromInput}
                  >
                    Apply
                  </button>
                </div>
              </div>,
              document.body,
            )}
        </div>
      </div>

      {/* Headings */}
      <div className="flex gap-1 border-r border-gray-600 pr-2">
        <IconTooltip label="Heading 1" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-700' : ''}`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            aria-label="Heading 1"
          >
            <Heading1 className="w-4 h-4" />
          </Button>
        </IconTooltip>
        <IconTooltip label="Heading 2" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-700' : ''}`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            aria-label="Heading 2"
          >
            <Heading2 className="w-4 h-4" />
          </Button>
        </IconTooltip>
      </div>

      {/* Text Alignment */}
      <div className="flex gap-1 border-r border-gray-600 pr-2">
        <IconTooltip label="Align left" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-700' : ''}`}
            onClick={() => setTextAlign('left')}
            aria-label="Align Left"
          >
            <AlignLeft className="w-4 h-4" />
          </Button>
        </IconTooltip>
        <IconTooltip label="Align center" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-700' : ''}`}
            onClick={() => setTextAlign('center')}
            aria-label="Align Center"
          >
            <AlignCenter className="w-4 h-4" />
          </Button>
        </IconTooltip>
        <IconTooltip label="Align right" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-700' : ''}`}
            onClick={() => setTextAlign('right')}
            aria-label="Align Right"
          >
            <AlignRight className="w-4 h-4" />
          </Button>
        </IconTooltip>
        <IconTooltip label="Justify" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${editor.isActive({ textAlign: 'justify' }) ? 'bg-gray-700' : ''}`}
            onClick={() => setTextAlign('justify')}
            aria-label="Justify"
          >
            <AlignJustify className="w-4 h-4" />
          </Button>
        </IconTooltip>
      </div>

      {/* Lists */}
      <div className="flex gap-1 border-r border-gray-600 pr-2">
        <IconTooltip label="Bullet list" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${editor.isActive('bulletList') ? 'bg-gray-700' : ''}`}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            aria-label="Bullet list"
          >
            <List className="w-4 h-4" />
          </Button>
        </IconTooltip>
        <IconTooltip label="Ordered list" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${editor.isActive('orderedList') ? 'bg-gray-700' : ''}`}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            aria-label="Ordered list"
          >
            <ListOrdered className="w-4 h-4" />
          </Button>
        </IconTooltip>
        <IconTooltip label="Todo list" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${editor.isActive('taskList') ? 'bg-gray-700' : ''}`}
            onClick={() => editor.chain().focus().toggleTaskList().run()}
            aria-label="Todo list"
          >
            <CheckSquare2 className="w-4 h-4" />
          </Button>
        </IconTooltip>
      </div>

      {/* Media & Code */}
      <div className="flex gap-1 border-gray-600 pr-2">
        <IconTooltip label="Code block" side="bottom">
          <Button
            className="btn btn-ghost p-1"
            aria-label="Code block"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            <Code className="w-4 h-4" />
          </Button>
        </IconTooltip>
        <IconTooltip label="Upload image" side="bottom">
          <Button
            className="btn btn-ghost p-1"
            aria-label="Upload image"
            onClick={onImageUpload}
          >
            <ImageIcon className="w-4 h-4" />
          </Button>
        </IconTooltip>
      </div>

      {/* Table Controls */}
      <div className="flex items-center gap-1 border-l border-gray-700 pl-2">
        <IconTooltip label="Insert table" side="bottom">
          <Button
            className={`btn btn-ghost p-1 ${isInTable ? 'bg-gray-700' : ''}`}
            aria-label="Insert table"
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
          >
            <Table2 className="w-4 h-4" />
          </Button>
        </IconTooltip>
        {isInTable ? (
          <>
            <IconTooltip label="Add row below" side="bottom">
              <Button
                className="btn btn-ghost p-1"
                aria-label="Add row below"
                onClick={() => editor.chain().focus().addRowAfter().run()}
              >
                <Rows3 className="w-4 h-4" />
              </Button>
            </IconTooltip>
            <IconTooltip label="Add column right" side="bottom">
              <Button
                className="btn btn-ghost p-1"
                aria-label="Add column right"
                onClick={() => editor.chain().focus().addColumnAfter().run()}
              >
                <Columns3 className="w-4 h-4" />
              </Button>
            </IconTooltip>
            <IconTooltip label="Delete row" side="bottom">
              <Button
                className="btn btn-ghost p-1"
                aria-label="Delete row"
                onClick={() => editor.chain().focus().deleteRow().run()}
              >
                <Rows3 className="w-4 h-4 text-red-400" />
              </Button>
            </IconTooltip>
            <IconTooltip label="Delete column" side="bottom">
              <Button
                className="btn btn-ghost p-1"
                aria-label="Delete column"
                onClick={() => editor.chain().focus().deleteColumn().run()}
              >
                <Columns3 className="w-4 h-4 text-red-400" />
              </Button>
            </IconTooltip>
            <IconTooltip label="Delete table" side="bottom">
              <Button
                className="btn btn-ghost p-1"
                aria-label="Delete table"
                onClick={() => editor.chain().focus().deleteTable().run()}
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </Button>
            </IconTooltip>
          </>
        ) : null}
      </div>

      {/* Authored Button */}
      <div className="border-r border-gray-600 pr-2">
        <IconTooltip label="Insert author information" side="bottom">
          <Button
            className="btn btn-ghost px-3 py-1 flex items-center gap-2"
            onClick={insertAuthored}
            aria-label="Insert author information"
          >
            <UserCheck className="w-4 h-4" />
            <span className="text-sm font-medium">Authored</span>
          </Button>
        </IconTooltip>
      </div>

      {/* History */}
      <div className="flex gap-1">
        <IconTooltip label="Undo" side="bottom">
          <Button
            className="btn btn-ghost p-1"
            aria-label="Undo"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            <Undo className="w-4 h-4" />
          </Button>
        </IconTooltip>
        <IconTooltip label="Redo" side="bottom">
          <Button
            className="btn btn-ghost p-1"
            aria-label="Redo"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            <Redo className="w-4 h-4" />
          </Button>
        </IconTooltip>
      </div>
    </div>
  );
};
